import { generateAIText, isAIConfigured } from './aiProvider';
import {
  getChimeraScan as getChimeraScanCore,
  SPECIAL_SCANS,
  type ChimeraScan,
} from './chimeraScanCore';
import type { Genre, Movie, TvShow } from '../types';

export type { ChimeraScan };

type MediaForScan = Movie | TvShow;

function getTitle(media: MediaForScan): string {
  return 'title' in media ? media.title : media.name;
}

function getOverview(media: MediaForScan): string {
  return media.overview || '';
}

function getGenres(media: MediaForScan): Genre[] {
  return media.genres || [];
}

export function getChimeraScan(media: MediaForScan, type: 'movie' | 'tv'): ChimeraScan {
  return getChimeraScanCore(media, type);
}

interface CachedChimeraScan {
  scan: ChimeraScan;
  timestamp: number;
}

const CHIMERA_SCAN_CACHE_PREFIX = 'cinevault_chimera_scan_v2';
const CHIMERA_SCAN_CACHE_MS = 30 * 24 * 60 * 60 * 1000;

function getCacheKey(media: MediaForScan, type: 'movie' | 'tv'): string {
  return `${CHIMERA_SCAN_CACHE_PREFIX}_${type}_${media.id}`;
}

function parseScanResponse(response: string): ChimeraScan | null {
  try {
    const json = response
      .replace(/```json\n?/gi, '')
      .replace(/```\n?/g, '')
      .trim();
    const match = json.match(/\{[\s\S]*\}/);
    if (!match) return null;
    const parsed = JSON.parse(match[0]) as Partial<ChimeraScan>;

    if (
      typeof parsed.signal !== 'string' ||
      typeof parsed.breath !== 'string' ||
      typeof parsed.membrane !== 'string' ||
      typeof parsed.rhythm !== 'string' ||
      typeof parsed.gift !== 'string' ||
      typeof parsed.lesson !== 'string' ||
      !Array.isArray(parsed.cluster)
    ) {
      return null;
    }

    return {
      signal: parsed.signal,
      breath: parsed.breath,
      membrane: parsed.membrane,
      rhythm: parsed.rhythm,
      gift: parsed.gift,
      lesson: parsed.lesson,
      cluster: parsed.cluster.filter((item): item is string => typeof item === 'string').slice(0, 4),
    };
  } catch (error) {
    console.error('Failed to parse CHIMERA scan:', error);
    return null;
  }
}

function loadCachedScan(cacheKey: string): ChimeraScan | null {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    const parsed = JSON.parse(cached) as CachedChimeraScan;
    if (Date.now() - parsed.timestamp > CHIMERA_SCAN_CACHE_MS) return null;
    return parsed.scan;
  } catch {
    return null;
  }
}

function saveCachedScan(cacheKey: string, scan: ChimeraScan): void {
  try {
    localStorage.setItem(cacheKey, JSON.stringify({ scan, timestamp: Date.now() } satisfies CachedChimeraScan));
  } catch {
    // Ignore localStorage quota/privacy failures. Fallback scans still work.
  }
}

export async function getEnhancedChimeraScan(media: MediaForScan, type: 'movie' | 'tv'): Promise<ChimeraScan> {
  const fallback = getChimeraScan(media, type);
  const special = SPECIAL_SCANS[`${type}:${media.id}`];
  if (special) return special;

  if (!isAIConfigured()) return fallback;

  const cacheKey = getCacheKey(media, type);
  const cached = loadCachedScan(cacheKey);
  if (cached) return cached;

  const title = getTitle(media);
  const genres = getGenres(media).map((genre) => genre.name).join(', ') || 'Unknown';
  const overview = getOverview(media) || 'No overview available.';

  const prompt = `Create a title-specific CHIMERA Body Scan for this ${type}.

Title: ${title}
Genres: ${genres}
Overview: ${overview}

CHIMERA language to use subtly:
- body, breath, membrane, rhythm, gift, signal, cluster
- O > I means the story gives more than it takes
- ¬F means no-force / the pause / respecting another body's sovereignty
- Many bodies can become one body without losing difference

Rules:
- Be specific to this title's premise. Do NOT sound generic.
- Mention concrete story dynamics from the overview when possible.
- Keep it useful for normal viewers, not academic.
- No spoilers beyond the overview.
- Each field should be one sentence, under 24 words.
- cluster should contain 3-4 short concept tags.

Return JSON only:
{
  "signal": "specific core pattern",
  "breath": "felt emotional atmosphere",
  "membrane": "boundary being tested",
  "rhythm": "movement pattern of the story",
  "gift": "what this story gives the viewer",
  "lesson": "life signal / human lesson",
  "cluster": ["tag", "tag", "tag"]
}`;

  const response = await generateAIText(prompt, { temperature: 0.82, maxTokens: 900 });
  const scan = parseScanResponse(response) || fallback;
  saveCachedScan(cacheKey, scan);
  return scan;
}
